/**
 * Conciliação bancária pela API de Extrato do Itaú.
 *
 * Busca os eventos do período direto no banco e os confronta com os
 * lançamentos existentes (data+valor) e com as decisões já registradas
 * (chaveadas no id único do evento — replay não duplica dinheiro).
 *
 * Decisões possíveis por evento novo:
 *   - criar lançamento (com a categoria sugerida pelas correlações de
 *     importação, editável);
 *   - já conciliado (o valor já está representado na conta);
 *   - divergência (reconhecida, sem lançamento).
 *
 * Um evento novo pode ser RATEADO entre categorias antes de virar lançamento:
 * as partes vão junto na decisão (`allocations`) e a categoria do item passa
 * a ser a principal (maior parte). O valor bancário é imutável — a soma das
 * partes tem de fechar exatamente |amountCents|.
 *
 * Débito com contraparte conhecida vem com o FORNECEDOR identificado (pelo
 * CNPJ/CPF do evento, por contraparte aprendida ou pelo nome) e com as
 * combinações de lançamentos existentes / parcelas em aberto dele que fecham
 * o valor — inclusive a soma de parcelas de vários pedidos num PIX só. Aceitar
 * vincula (decisão 'supplier'): lançamentos existentes são mantidos, parcelas
 * viram um lançamento cada. Sem sugestão, "Vincular fornecedor" abre o modal.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, CheckCircle2, AlertTriangle, Sparkles, Undo2, Split, Link2 } from 'lucide-react';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { Card, CardContent } from '@components/Card';
import bankIntegrationService from '@services/bankIntegrationService';
import { providerService } from '@services/providerService';
import type { Provider } from '@/types/provider';
import { formatCurrencyBRL, formatDateBR, filterCategoryGroupsByMovement, CategoryOptionGroup } from './financialUtils';
import { SupplierPaymentLinkModal } from './SupplierPaymentLinkModal';
import { SUPPLIER_SOURCE_LABEL, buildSupplierDecision, candidateLabel, suggestionKindLabel } from './bankStatementSupplier';
import { inputCls, CategorySelect, SummaryCard, ModalShell, primaryBtnCls, secondaryBtnCls } from './financialShared';
import { AllocationEditor } from './AllocationEditor';
import {
  allocationsValidationMessage,
  newAllocationLine,
  principalErpId,
  toAllocationPayload,
  type AllocationLine,
} from './allocationCalc';
import type {
  BankStatementDecisionItem,
  BankStatementPreview,
  BankStatementPreviewItem,
  SupplierPaymentSuggestion,
} from '@/types/bankIntegration';

interface ItauStatementReconciliationPanelProps {
  /** AccountId (PK do cadastro) — os endpoints da integração usam ele, não o
   *  idAccountsMain do extrato. */
  accountId: number;
  accountLabel: string;
  categoryGroups: CategoryOptionGroup[];
}

const formatCents = (cents: number): string => formatCurrencyBRL(cents / 100);

const isoDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const STATUS_META: Record<string, { label: string; className: string }> = {
  ja_lancado: { label: 'Conciliado', className: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  novo: { label: 'Novo', className: 'bg-amber-50 border-amber-200 text-amber-700' },
  decidido: { label: 'Decidido', className: 'bg-slate-50 border-slate-200 text-slate-600' },
};

const DECISION_LABEL: Record<string, string> = {
  created: 'lançamento criado',
  reconciled: 'marcado como conciliado',
  divergent: 'divergência registrada',
  supplier: 'vinculado a fornecedor',
};

export const ItauStatementReconciliationPanel: React.FC<ItauStatementReconciliationPanelProps> = ({
  accountId,
  accountLabel,
  categoryGroups,
}) => {
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(7));
  const [dateTo, setDateTo] = useState(isoDaysAgo(0));

  const [preview, setPreview] = useState<BankStatementPreview | null>(null);
  // A varredura da aplicação automática (Aplic Aut) é movimento do banco, não
  // do usuário — vem oculta por padrão para a tela mostrar só o que ele
  // controla. Os cards somam apenas o que está visível.
  const [showAutoInvestment, setShowAutoInvestment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // O erro aparece no topo do painel; quem clicou numa linha lá embaixo não o via
  // ("cliquei em Já conciliado e nada aconteceu"). Rola até ele.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  // Seleção e categoria por evento — a sugestão das correlações entra como
  // valor inicial e continua editável.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [categoryByEvent, setCategoryByEvent] = useState<Record<string, number | null>>({});

  // Rateio por evento — só existe para quem o usuário rateou (ausente = uma
  // categoria só). O modal edita um rascunho e só grava ao "Aplicar".
  const [allocationsByEvent, setAllocationsByEvent] = useState<Record<string, AllocationLine[]>>({});
  const [allocModal, setAllocModal] = useState<{ item: BankStatementPreviewItem; lines: AllocationLine[] } | null>(null);

  // Vínculo com fornecedor: o modal precisa da lista de fornecedores (carregada
  // na primeira abertura) e pode nascer de uma sugestão da tabela.
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [supplierModal, setSupplierModal] = useState<{
    item: BankStatementPreviewItem;
    initialProviderId: number | null;
    initialSelection: SupplierPaymentSuggestion | null;
  } | null>(null);

  // Crédito só recebe categoria de Entrada; débito só de Saída (movement 2 /
  // legado aparece nos dois). O select de cada linha usa o conjunto do sinal.
  const entradaGroups = useMemo(() => filterCategoryGroupsByMovement(categoryGroups, 1), [categoryGroups]);
  const saidaGroups = useMemo(() => filterCategoryGroupsByMovement(categoryGroups, 0), [categoryGroups]);

  const groupsFor = (amountCents: number): CategoryOptionGroup[] =>
    amountCents < 0 ? saidaGroups : entradaGroups;

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotice(null);
      const data = await bankIntegrationService.previewStatement(accountId, dateFrom, dateTo);
      setPreview(data);
      const categories: Record<string, number | null> = {};
      const preselect = new Set<string>();
      for (const item of data.items) {
        if (item.status !== 'novo' || item.autoInvestment) continue;
        // A sugestão da correlação só vale se a categoria for do movimento
        // certo — sugerir Entrada para um débito criaria lançamento invisível
        // no select e classificado errado.
        const allowed = item.amountCents < 0 ? saidaGroups : entradaGroups;
        const suggestion = item.suggestedAccountingPlanErpId ?? null;
        const suggestionValid =
          suggestion != null && allowed.some((g) => g.options.some((o) => o.value === suggestion));
        categories[item.providerEventId] = suggestionValid ? suggestion : null;
        preselect.add(item.providerEventId);
      }
      setCategoryByEvent(categories);
      setSelected(preselect);
      // Rateio só faz sentido para evento ainda novo — o que foi decidido (ou
      // saiu do período) perde o rascunho.
      setAllocationsByEvent((prev) => {
        const next: Record<string, AllocationLine[]> = {};
        for (const id of preselect) if (prev[id]) next[id] = prev[id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao consultar o extrato');
    } finally {
      setLoading(false);
    }
  };

  // Itens visíveis: sem a aplicação automática, a menos que o usuário peça.
  const visibleItems = useMemo(
    () => (preview?.items ?? []).filter((i) => showAutoInvestment || !i.autoInvestment),
    [preview, showAutoInvestment],
  );
  const hiddenAutoCount = (preview?.items.length ?? 0) - visibleItems.length;

  // Os cards refletem o que está na tabela — esconder a Aplic Aut e continuar
  // somando os débitos dela nos cards seria mentir duas vezes.
  const summary = useMemo(() => {
    const s = { credit: 0, debit: 0, matched: 0, novo: 0, decided: 0 };
    for (const i of visibleItems) {
      if (i.amountCents >= 0) s.credit += i.amountCents;
      else s.debit += i.amountCents;
      if (i.status === 'ja_lancado') s.matched++;
      else if (i.status === 'novo') s.novo++;
      else s.decided++;
    }
    return s;
  }, [visibleItems]);

  const newItems = useMemo(
    () => visibleItems.filter((i) => i.status === 'novo'),
    [visibleItems],
  );

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Rateio ──────────────────────────────────────────────────────────

  const openAllocation = (item: BankStatementPreviewItem) => {
    const existing = allocationsByEvent[item.providerEventId];
    // Começa com a categoria já escolhida na primeira parte — o usuário só
    // completa o restante.
    const lines: AllocationLine[] = existing
      ? existing.map((l) => ({ ...l }))
      : [
          { ...newAllocationLine(), accountingPlanErpId: categoryByEvent[item.providerEventId] ?? null },
          newAllocationLine(),
        ];
    setAllocModal({ item, lines });
  };

  const applyAllocation = () => {
    if (!allocModal) return;
    const { item, lines } = allocModal;
    if (allocationsValidationMessage(Math.abs(item.amountCents), lines) !== null) return;
    setAllocationsByEvent((prev) => ({ ...prev, [item.providerEventId]: lines }));
    // A principal fica espelhada no estado de categoria — se o rateio for
    // limpo, o select volta mostrando ela.
    setCategoryByEvent((prev) => ({ ...prev, [item.providerEventId]: principalErpId(lines) }));
    setAllocModal(null);
  };

  const clearAllocation = (providerEventId: string) => {
    setAllocationsByEvent((prev) => {
      const next = { ...prev };
      delete next[providerEventId];
      return next;
    });
    setAllocModal(null);
  };

  const applyDecisions = async (items: BankStatementDecisionItem[], successPrefix: string) => {
    try {
      setApplying(true);
      setError(null);
      const result = await bankIntegrationService.sendDecisions(accountId, items);
      // O que virou lançamento não tem mais rateio a editar.
      setAllocationsByEvent((prev) => {
        const next = { ...prev };
        for (const it of items) delete next[it.providerEventId];
        return next;
      });
      const parts: string[] = [];
      if (result.createdCount > 0) parts.push(`${result.createdCount} lançamento(s) criado(s)`);
      if (result.reconciledCount > 0) parts.push(`${result.reconciledCount} conciliado(s)`);
      if (result.divergentCount > 0) parts.push(`${result.divergentCount} divergência(s)`);
      if (result.supplierLinkedCount) {
        const details: string[] = [];
        if (result.supplierCreatedCount) details.push(`${result.supplierCreatedCount} lançamento(s) criado(s) de parcelas`);
        if (result.supplierMovedCount) details.push(`${result.supplierMovedCount} lançamento(s) com a data movida para a do banco`);
        parts.push(`${result.supplierLinkedCount} vinculado(s) a fornecedor${details.length ? ` (${details.join(', ')})` : ''}`);
      }
      if (result.skippedCount > 0) parts.push(`${result.skippedCount} já decidido(s) antes`);
      if (result.autoSettle && result.autoSettle.settledGroups > 0) {
        parts.push(
          `baixa automática: ${result.autoSettle.settledReceivables} parcela(s) de cartão liquidada(s) (${formatCents(result.autoSettle.settledNetCents)})`,
        );
      }
      setNotice(`${successPrefix}: ${parts.join(', ') || 'nada a fazer'}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao aplicar as decisões');
    } finally {
      setApplying(false);
    }
  };

  const createSelected = () => {
    const items: BankStatementDecisionItem[] = newItems
      .filter((i) => selected.has(i.providerEventId))
      .map((i) => {
        const lines = allocationsByEvent[i.providerEventId];
        return {
          providerEventId: i.providerEventId,
          date: i.date,
          description: i.description,
          amountCents: i.amountCents,
          decision: 'created',
          // Com rateio, a categoria do item é a PRINCIPAL (maior parte) — o
          // back recalcula, mas mandar já certo evita divergência.
          accountingPlanErpId: lines ? principalErpId(lines) : (categoryByEvent[i.providerEventId] ?? null),
          ...(lines ? { allocations: toAllocationPayload(lines) } : {}),
        };
      });
    if (items.length === 0) {
      setError('Selecione ao menos um evento para lançar');
      return;
    }
    void applyDecisions(items, 'Decisões aplicadas');
  };

  // ── Fornecedor ─────────────────────────────────────────────────────

  const ensureProviders = async () => {
    if (providers) return;
    try {
      const res = await providerService.getProviders({ page: 1, pageSize: 500 });
      setProviders(res.data.filter((p) => p.is_active));
    } catch {
      setProviders([]);
    }
  };

  const openSupplierModal = (item: BankStatementPreviewItem, suggestion: SupplierPaymentSuggestion | null) => {
    void ensureProviders();
    setSupplierModal({ item, initialProviderId: item.supplier?.providerId ?? null, initialSelection: suggestion });
  };

  // Aceitar a sugestão direto da tabela: a combinação fecha o valor ao centavo
  // (o backend confere de novo). Sem parcela não há categoria a escolher; com
  // parcela a categoria do pedido é o default — quem quiser outra usa o modal.
  const acceptSuggestion = (item: BankStatementPreviewItem, suggestion: SupplierPaymentSuggestion) => {
    if (!item.supplier) return;
    const decision = buildSupplierDecision(item, item.supplier.providerId, suggestion.items, null, false);
    void applyDecisions([decision], 'Evento vinculado ao fornecedor');
  };

  const submitSupplierLink = (decision: BankStatementDecisionItem) => {
    setSupplierModal(null);
    void applyDecisions([decision], 'Evento vinculado ao fornecedor');
  };

  const decideSingle = (item: BankStatementPreviewItem, decision: 'reconciled' | 'divergent') => {
    void applyDecisions(
      [
        {
          providerEventId: item.providerEventId,
          date: item.date,
          description: item.description,
          amountCents: item.amountCents,
          decision,
        },
      ],
      decision === 'reconciled' ? 'Evento conciliado' : 'Divergência registrada',
    );
  };

  // Desfaz decisão sem lançamento ('Já conciliado'/'Divergência') — o evento
  // volta a "novo". Decisão 'Lançado' não tem desfazer: o lançamento existe.
  const undoDecision = async (item: BankStatementPreviewItem) => {
    if (!window.confirm(`Desfazer a decisão de "${item.description}"? O evento volta a aparecer como novo.`)) return;
    setApplying(true);
    setError(null);
    setNotice(null);
    try {
      await bankIntegrationService.undoDecision(accountId, item.providerEventId);
      setNotice('Decisão desfeita — o evento voltou a ficar pendente');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao desfazer a decisão');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Consulta o extrato de <strong>{accountLabel}</strong> direto na API do Itaú e compara com os
        lançamentos da conta. Eventos novos podem virar lançamento (com a categoria sugerida pelas
        correlações de importação); créditos da Cielo que conferem com a agenda liquidam as parcelas
        automaticamente.
      </p>

      {error && (
        <div ref={errorRef}>
          <Card className="bg-red-50 border-red-200">
            <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
          </Card>
        </div>
      )}
      {notice && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent><p className="text-emerald-700 text-sm">{notice}</p></CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">De</label>
              <DateInputBR className={inputCls} value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Até</label>
              <DateInputBR className={inputCls} value={dateTo} onChange={setDateTo} />
            </div>
            <Button size="sm" loading={loading} onClick={load} icon={<RefreshCw size={14} />}>
              Consultar extrato
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => { setDateFrom(isoDaysAgo(0)); setDateTo(isoDaysAgo(0)); }}
            >
              Hoje
            </button>
            <span className="text-xs text-slate-300">·</span>
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => { setDateFrom(isoDaysAgo(7)); setDateTo(isoDaysAgo(0)); }}
            >
              Últimos 7 dias
            </button>
            <span className="text-xs text-slate-300">·</span>
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => { setDateFrom(isoDaysAgo(30)); setDateTo(isoDaysAgo(0)); }}
            >
              Últimos 30 dias
            </button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryCard label="Créditos no período" value={formatCents(summary.credit)} valueClassName="text-emerald-700" />
            {/* O total de débitos é soma negativa; o card mostra o módulo — o
                vermelho já diz a direção do dinheiro. */}
            <SummaryCard label="Débitos no período" value={formatCents(Math.abs(summary.debit))} valueClassName="text-red-600" />
            <SummaryCard label="Conciliados" value={String(summary.matched)} />
            <SummaryCard label="Novos" value={String(summary.novo)} valueClassName={summary.novo > 0 ? 'text-amber-600' : undefined} />
            <SummaryCard label="Decididos" value={String(summary.decided)} />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={showAutoInvestment}
                onChange={(e) => setShowAutoInvestment(e.target.checked)}
              />
              Mostrar aplicação automática (Aplic Aut)
            </label>
            {!showAutoInvestment && hiddenAutoCount > 0 && (
              <span className="text-xs text-slate-400">
                {hiddenAutoCount} lançamento(s) de aplicação automática oculto(s)
              </span>
            )}
          </div>

          {newItems.length > 0 && (
            <div className="flex justify-end">
              <Button size="sm" loading={applying} onClick={createSelected} icon={<Sparkles size={14} />}>
                Criar lançamentos ({[...selected].filter((id) => newItems.some((i) => i.providerEventId === id)).length})
              </Button>
            </div>
          )}

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <th className="text-left font-medium py-2.5 px-4 w-8"></th>
                    <th className="text-left font-medium py-2.5">Data</th>
                    <th className="text-left font-medium py-2.5">Descrição</th>
                    <th className="text-right font-medium py-2.5">Valor</th>
                    <th className="text-left font-medium py-2.5 pl-4">Situação</th>
                    <th className="text-left font-medium py-2.5">Categoria</th>
                    <th className="text-right font-medium py-2.5 px-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-slate-500 py-8">
                        {preview.items.length > 0
                          ? 'Só há lançamentos de aplicação automática no período — marque a opção acima para vê-los.'
                          : 'O banco não devolveu eventos no período.'}
                      </td>
                    </tr>
                  ) : (
                    visibleItems.map((item) => {
                      const meta = STATUS_META[item.status] ?? STATUS_META.decidido;
                      const isNew = item.status === 'novo';
                      const allocation = isNew ? allocationsByEvent[item.providerEventId] : undefined;
                      return (
                        <tr key={item.providerEventId} className="border-b border-slate-100">
                          <td className="py-2.5 px-4">
                            {isNew && (
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                checked={selected.has(item.providerEventId)}
                                onChange={() => toggleSelected(item.providerEventId)}
                              />
                            )}
                          </td>
                          <td className="py-2.5 whitespace-nowrap">{formatDateBR(item.date)}</td>
                          <td className="py-2.5">
                            <span className="text-slate-800">{item.description}</span>
                            {item.counterpart && (
                              <span className="block text-xs text-slate-500">{item.counterpart}</span>
                            )}
                            {isNew && item.amountCents < 0 && item.supplier && (
                              <span
                                className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
                                title={`Fornecedor identificado ${SUPPLIER_SOURCE_LABEL[item.supplier.source] || ''}`}
                              >
                                <Link2 size={11} /> {item.supplier.name}
                              </span>
                            )}
                            {isNew && item.amountCents < 0 && (item.suggestions?.length ?? 0) > 0 && (
                              <span className="mt-1 flex flex-col gap-1">
                                {item.suggestions!.map((s, idx) => (
                                  <span
                                    key={idx}
                                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800"
                                  >
                                    <Sparkles size={11} className="shrink-0" />
                                    <span className="font-medium">{suggestionKindLabel(s)}:</span>
                                    <span>{s.items.map((c) => `${candidateLabel(c)} ${formatCents(c.amountCents)}`).join(' + ')}</span>
                                    <button
                                      type="button"
                                      className="ml-auto rounded border border-emerald-400 bg-white px-2 py-0.5 font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                      disabled={applying}
                                      title="Vincula o evento a estes itens (lançamentos existentes são mantidos; parcelas viram lançamento)"
                                      onClick={() => acceptSuggestion(item, s)}
                                    >
                                      Aceitar
                                    </button>
                                    <button
                                      type="button"
                                      className="text-emerald-700 hover:underline disabled:opacity-50"
                                      disabled={applying}
                                      onClick={() => openSupplierModal(item, s)}
                                    >
                                      escolher…
                                    </button>
                                  </span>
                                ))}
                              </span>
                            )}
                            <span className="flex gap-1 mt-0.5">
                              {item.originType && (
                                <span className="inline-flex px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-medium text-slate-500">
                                  {item.originType}
                                </span>
                              )}
                              {item.reversal && (
                                <span className="inline-flex px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-[10px] font-medium text-red-600">
                                  Estorno
                                </span>
                              )}
                              {item.autoInvestment && (
                                <span className="inline-flex px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-[10px] font-medium text-sky-600">
                                  Aplic Aut
                                </span>
                              )}
                              {(item.groupedCount ?? 0) > 0 && (
                                <span
                                  className="inline-flex px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-[10px] font-medium text-indigo-600"
                                  title="Pagamento em lote: o banco mostra todos os pagamentos como uma linha só"
                                >
                                  Lote · {item.groupedCount} pagamentos
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={`py-2.5 text-right font-medium whitespace-nowrap ${item.amountCents < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            {formatCents(item.amountCents)}
                            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${item.amountCents < 0 ? 'text-red-400' : 'text-emerald-500'}`}>
                              {item.amountCents < 0 ? 'Débito' : 'Crédito'}
                            </span>
                          </td>
                          <td className="py-2.5 pl-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.className}`}>
                              {item.status === 'ja_lancado' ? <CheckCircle2 size={13} /> : item.status === 'novo' ? <AlertTriangle size={13} /> : <Undo2 size={13} />}
                              {meta.label}
                            </span>
                            {item.status === 'decidido' && item.decision && (
                              <span className="block text-xs text-slate-500 mt-0.5">
                                {DECISION_LABEL[item.decision] ?? item.decision}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 min-w-56">
                            {isNew ? (
                              allocation ? (
                                // Rateado: a principal é derivada da maior parte —
                                // não há select a mostrar.
                                <span className="inline-flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-xs font-medium text-indigo-700">
                                    <Split size={12} /> Rateado · {allocation.length}
                                  </span>
                                  <button
                                    type="button"
                                    className="text-xs text-emerald-700 hover:underline disabled:opacity-50"
                                    disabled={applying}
                                    onClick={() => openAllocation(item)}
                                  >
                                    editar
                                  </button>
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <CategorySelect
                                    groups={groupsFor(item.amountCents)}
                                    value={categoryByEvent[item.providerEventId] ?? null}
                                    onChange={(erpId) =>
                                      setCategoryByEvent((prev) => ({ ...prev, [item.providerEventId]: erpId }))
                                    }
                                    emptyLabel="— sem categoria —"
                                  />
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50"
                                    disabled={applying}
                                    title="Dividir o valor entre duas ou mais categorias"
                                    onClick={() => openAllocation(item)}
                                  >
                                    <Split size={12} /> Ratear
                                  </button>
                                </span>
                              )
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-right whitespace-nowrap">
                            {isNew && (
                              <span className="inline-flex gap-2">
                                {item.amountCents < 0 && (
                                  <button
                                    className="text-xs text-violet-700 hover:underline disabled:opacity-50"
                                    disabled={applying}
                                    title="Vincula o débito a lançamentos existentes e/ou parcelas em aberto de um fornecedor"
                                    onClick={() => openSupplierModal(item, null)}
                                  >
                                    Vincular fornecedor
                                  </button>
                                )}
                                <button
                                  className="text-xs text-emerald-700 hover:underline disabled:opacity-50"
                                  disabled={applying}
                                  title="O valor já está representado na conta — registra o vínculo sem criar lançamento"
                                  onClick={() => decideSingle(item, 'reconciled')}
                                >
                                  Já conciliado
                                </button>
                                <button
                                  className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                  disabled={applying}
                                  title="Reconhece a divergência sem criar lançamento"
                                  onClick={() => decideSingle(item, 'divergent')}
                                >
                                  Divergência
                                </button>
                              </span>
                            )}
                            {item.status === 'decidido' &&
                              (item.decision === 'reconciled' || item.decision === 'divergent' || item.decision === 'supplier') && (
                                <button
                                  className="text-xs text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-50"
                                  disabled={applying}
                                  title="Apaga a decisão registrada e o evento volta a aparecer como novo (vínculo com fornecedor: restaura a data dos lançamentos; se gerou lançamento de parcela, não desfaz)"
                                  onClick={() => void undoDecision(item)}
                                >
                                  Desfazer
                                </button>
                              )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {supplierModal && (
        <SupplierPaymentLinkModal
          accountId={accountId}
          item={supplierModal.item}
          providers={providers ?? []}
          categoryGroups={saidaGroups}
          initialProviderId={supplierModal.initialProviderId}
          initialSelection={supplierModal.initialSelection}
          submitting={applying}
          onClose={() => setSupplierModal(null)}
          onSubmit={submitSupplierLink}
        />
      )}

      {allocModal && (() => {
        const { item, lines } = allocModal;
        const totalCents = Math.abs(item.amountCents);
        const validation = allocationsValidationMessage(totalCents, lines);
        const hasApplied = Boolean(allocationsByEvent[item.providerEventId]);
        return (
          <ModalShell
            title={`Ratear ${item.description} · ${formatCents(totalCents)}`}
            icon={<Split size={18} className="text-indigo-600" />}
            onClose={() => setAllocModal(null)}
            maxWidth="max-w-3xl"
            footer={
              <>
                {hasApplied && (
                  <button
                    type="button"
                    className="mr-auto text-sm text-red-600 hover:underline"
                    onClick={() => clearAllocation(item.providerEventId)}
                  >
                    Limpar rateio
                  </button>
                )}
                <button type="button" className={secondaryBtnCls} onClick={() => setAllocModal(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={primaryBtnCls}
                  disabled={validation !== null}
                  title={validation ?? undefined}
                  onClick={applyAllocation}
                >
                  Aplicar
                </button>
              </>
            }
          >
            <p className="text-sm text-slate-600">
              O valor do evento é o do banco e não muda: a soma das partes precisa fechar{' '}
              <strong>{formatCents(totalCents)}</strong>. A categoria principal do lançamento será a da maior parte.
            </p>
            <AllocationEditor
              totalCents={totalCents}
              categoryGroups={groupsFor(item.amountCents)}
              lines={lines}
              onChange={(next) => setAllocModal((prev) => (prev ? { ...prev, lines: next } : prev))}
            />
          </ModalShell>
        );
      })()}
    </div>
  );
};
